import { SharedGridStyleBaseColor } from "features/CenterContent/shared/MainContentGrid";
import { PanelActions } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import {
  BusModule,
  PanelHeaderDivider,
  PanelHorizontalScroll,
  PanelModuleGrid,
  PanelScrollArea,
  PanelScrollContent,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { BusGps } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/BusGps/BusGps";
import { IncidentsAndStopsPanels } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/IncidentsAndStopsPanels";
import { Itinerary } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/Itinerary";
import { useVehicleCancellationAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { useVehicleUndoCancellationAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleUndoCancellation";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import { useVehiclePhoneNumberAction } from "features/CenterContent/VehicleDetails/shared/VehiclePhoneNumber";
import { useMemo } from "react";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import { Box, Text, VStack } from "shared/components/Spor";
import {
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
  StopReference,
} from "shared/types/alternativeTransport";

interface AlternativeTransportRightPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportRightPanel = ({
  vehicle,
}: AlternativeTransportRightPanelProps) => {
  const trainNumberTurnicate =
    vehicle.trainIds.join(", ").length > 16
      ? `${vehicle.trainIds.join(", ").substring(0, 16)}...`
      : vehicle.trainIds.join(", ");

  const distributionStopPlaces = useMemo(
    () =>
      vehicle.itinerary
        ? [...vehicle.itinerary.stops, ...(vehicle.itinerary.returnStops ?? [])]
            .filter((stop) => !!stop.stopReference.nsrStopPlace)
            .map((stop) => stop.stopReference.nsrStopPlace)
            .join(",")
        : "",
    [vehicle],
  );

  const shiftId = vehicle.workShiftPlan?.shiftId;
  const departureNumber = `${shiftId ? `${shiftId}-` : ""}${trainNumberTurnicate}${
    trainNumberTurnicate && !shiftId ? " Operative" : ""
  }`;

  const specification = vehicle.isPartOfShuttlePlan ? "Pendel" : undefined;

  const phoneAction = useVehiclePhoneNumberAction({
    phoneNumberUpdate: vehicle.driverPhoneNumber,
    workShift: vehicle.workShiftPlan,
    id: vehicle.id,
    operatorUpdate: {
      operatorName: vehicle.operatorName,
      operatorId: vehicle.operatorId,
    },
  });
  const cancellationAction = useVehicleCancellationAction(vehicle);
  const undoCancellationAction = useVehicleUndoCancellationAction(vehicle);

  const actions = [
    phoneAction,
    cancellationAction,
    undoCancellationAction,
  ].filter((action) => action !== null);

  const distributionStopReferences: Array<StopReference> = useMemo(() => {
    const stops = vehicle?.itinerary?.stops ?? [];
    const returnStops = vehicle?.itinerary?.returnStops ?? [];
    return [...stops, ...returnStops].map(
      (value: AlternativeTransportItineraryStop) => value.stopReference,
    );
  }, [vehicle]);

  return (
    <BusModule
      modulePosition="right"
      title={<Text variant="md-lg">{`Avgang: ${departureNumber ?? ""}`}</Text>}
      specification={specification}
      action1={
        <PanelActions overflowActions={actions} inlineActions={actions} />
      }
    >
      <PanelHeaderDivider>
        <Itinerary alternativeTransport={vehicle} />
      </PanelHeaderDivider>
      <PanelHorizontalScroll>
        <PanelScrollContent>
          <PanelScrollArea>
            <PanelModuleGrid>
              <Module
                title="Trasé"
                disableHorizontalPadding
                disableBottomPadding
              >
                <IncidentsAndStopsPanels
                  vehicle={vehicle}
                  distributionStopReferences={distributionStopReferences}
                />
              </Module>
              <VStack gap={2} align="stretch">
                <Box flexShrink="0">
                  <Module title="Info fra Brudd">
                    {vehicle.notes.length > 0 ? (
                      <div style={{ width: "100%" }}>
                        <ExpandableInfoMessage
                          semantics="info"
                          title="Info fra Brudd"
                        >
                          {vehicle.notes.map((note) => (
                            <Text key={note}>{note}</Text>
                          ))}
                        </ExpandableInfoMessage>
                      </div>
                    ) : (
                      <NoDataMessage
                        panelStyle={SharedGridStyleBaseColor}
                        reason="Det finnes ikke hendelse denne bussen"
                      />
                    )}
                  </Module>
                </Box>
                <Module title="Logg">
                  <OperationalLog
                    vehicleId={vehicle.id}
                    logs={vehicle.logs}
                    distributionStopPlaces={distributionStopPlaces}
                  />
                </Module>
                <Module title="GPS-data">
                  <BusGps vehicle={vehicle} />
                </Module>
              </VStack>
            </PanelModuleGrid>
          </PanelScrollArea>
        </PanelScrollContent>
      </PanelHorizontalScroll>
    </BusModule>
  );
};
